import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/page-shell";
import { ArticleBody, DotItem, DotList, EffectiveDate, Section } from "@/components/guide-page";
import { absUrl } from "@/lib/site";

const EFFECTIVE_DATE = "July 20, 2026";

export const Route = createFileRoute("/cookies")({
  head: () => ({
    meta: [
      { title: "Cookie & Tracking Notice | LoneStarMap" },
      {
        name: "description",
        content:
          "The cookies, browser storage, and analytics LoneStarMap uses, and how to opt out.",
      },
      { property: "og:title", content: "Cookie & Tracking Notice" },
      {
        property: "og:description",
        content: "What LoneStarMap stores in your browser and how to opt out.",
      },
      { property: "og:url", content: absUrl("/cookies") },
    ],
    links: [{ rel: "canonical", href: absUrl("/cookies") }],
  }),
  component: CookiesPage,
});

const LIST = "text-base leading-[1.7] text-foreground/85";
const BODY = "measure text-base leading-[1.7] text-foreground/85";

function CookiesPage() {
  return (
    <PageShell
      title="Cookie & Tracking Notice"
      intro="LoneStarMap uses a very small number of cookies and browser-storage items. We do not use advertising cookies and we do not sell your data. This notice explains what we store and how to opt out."
    >
      <EffectiveDate date={EFFECTIVE_DATE} />

      <ArticleBody>
        <p className={BODY}>
          Read it together with our{" "}
          <Link to="/privacy" className="link">
            Privacy Policy
          </Link>
          .
        </p>

        <Section title="Functional">
          <p className="measure -mt-2 text-sm text-muted-foreground">
            The site works without them, but some features degrade.
          </p>
          <DotList className={LIST}>
            <DotItem>
              <strong className="font-semibold text-foreground">Bite log:</strong> tick-bite entries
              you save stay in your browser&rsquo;s local storage. They are never uploaded to us or
              anyone else.
            </DotItem>
            <DotItem>
              <strong className="font-semibold text-foreground">Theme preference:</strong> remembers
              dark/light mode selection.
            </DotItem>
          </DotList>
        </Section>

        <Section title="Analytics">
          <DotList className={LIST}>
            <DotItem>
              <strong className="font-semibold text-foreground">Google Analytics 4</strong> (
              <code>_ga</code>, <code>_ga_*</code>): measures page views, referrer, approximate
              location, and device type in aggregate. Google anonymizes IP addresses by default. We
              use it to understand what content is useful.
            </DotItem>
          </DotList>
          <p className={BODY}>
            You can opt out of Google Analytics by installing the{" "}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noopener noreferrer"
              className="link"
            >
              Google Analytics Opt-out Browser Add-on
            </a>
            , by enabling your browser&rsquo;s &ldquo;Do Not Track&rdquo; or Global Privacy Control
            setting, or by using a privacy-focused browser or extension that blocks analytics
            scripts.
          </p>
        </Section>

        <Section title="What we do not use">
          <DotList
            className={LIST}
            items={[
              "Advertising or retargeting cookies.",
              "Social-media tracking pixels.",
              "Cross-site behavioral tracking.",
            ]}
          />
        </Section>

        <Section title="Managing cookies">
          <p className={BODY}>
            You can clear or block cookies through your browser settings at any time. Blocking
            functional storage may cause some Site features (like duplicate-submission detection) to
            behave inconsistently, but the Site will still load.
          </p>
        </Section>
      </ArticleBody>
    </PageShell>
  );
}
